import { FieldValues, Path, PathValue } from 'react-hook-form';
import { TextFieldType } from '../../../types/form';
import {
  useTextFieldController,
  UseTextFieldControllerProps,
} from '../../../hooks/form/useTextFieldController';
import TextInput, { TextInputProps } from '../../library/TextInput';
import ErrorMessage from '../shared/ErrorMessage';

type TextInputPropsWithControllerProps<TFieldValues> =
  UseTextFieldControllerProps<TFieldValues> &
    Omit<
      TextInputProps,
      | 'name'
      | 'value'
      | 'defaultValue'
      | 'disabled'
      | 'onChange'
      | 'onBlur'
      | 'required'
      | 'error'
    >;

export interface TextFieldProps<TFieldValues>
  extends TextInputPropsWithControllerProps<TFieldValues> {
  type?: TextFieldType;
}

export default function TextField<TFieldValues extends FieldValues>(
  props: TextFieldProps<TFieldValues>,
) {
  const {
    type = 'text',
    name,
    onChange,
    onBlur,
    helperText,
    defaultValue = '',
    rules,
    shouldUnregister,
    disabled,
    fullWidth = true,
    ...restTextFieldProps
  } = props;

  const {
    field,
    required,
    onBlurField,
    onChangeField,
    formState: { errors },
    fieldState: { invalid: error },
  } = useTextFieldController<TFieldValues>({
    name,
    type,
    onChange,
    onBlur,
    defaultValue: defaultValue as PathValue<TFieldValues, Path<TFieldValues>>,
    rules,
    shouldUnregister,
    disabled,
  });

  return (
    <TextInput
      type={type}
      name={field.name}
      value={field.value}
      disabled={field.disabled}
      inputRef={field.ref}
      onChange={onChangeField}
      onBlur={onBlurField}
      error={error}
      helperText={
        error ? <ErrorMessage name={field.name} errors={errors} /> : helperText
      }
      required={required}
      fullWidth={fullWidth}
      {...restTextFieldProps}
    />
  );
}
